import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { coursesService, sessionsService } from '../../services/firestoreService';
import { newCourseData } from '../../utils/newCourseData';
import './AddPythonCourse.css';

/**
 * Default session: starts in 7 days, runs for 12 weeks. Total duration = sum of topic hours.
 */
function getDefaultSessionDates() {
  const start = new Date();
  start.setDate(start.getDate() + 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 12 * 7); // 12 weeks
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10)
  };
}

const AddNewCourse = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleAddCourseAndSession = async () => {
    if (!user || user.type !== 'admin') {
      setMessage('❌ Only admins can add courses.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const creatorId = user.id;
      const creatorName = user.name || user.email?.split('@')[0] || 'Admin';
      const creatorEmail = user.email || '';

      const courseData = {
        ...newCourseData,
        topics: newCourseData.topics.map((t) => ({
          name: t.name,
          description: t.description,
          hours: Math.max(2, t.hours || 2),
          date: '',
          sampleCode: ''
        })),
        createdBy: creatorId,
        createdByName: creatorName,
        createdByEmail: creatorEmail
      };

      const courseId = await coursesService.createCourse(courseData);
      const totalDuration = courseData.topics.reduce((sum, t) => sum + (t.hours || 2), 0);
      const { startDate, endDate } = getDefaultSessionDates();

      const sessionData = {
        title: `${newCourseData.title} - Session 1`,
        startDate,
        endDate,
        totalDuration,
        courseId,
        courseObjects: newCourseData.description || '',
        expectations: '',
        topicDates: {},
        sessionCost: 400,
        costSchedule: '50% at the beginning, 50% after 4th week',
        feesTerms: '$400 total. 50% at the beginning, 50% after 4th week.'
      };

      await sessionsService.createSession(sessionData);
      setMessage('✅ Course created with 10 topics and one session. You can edit both in Courses and Sessions Management.');
    } catch (error) {
      console.error('Error adding course/session:', error);
      setMessage(`❌ Error: ${error.message || 'Failed to add course and session'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.type !== 'admin') {
    return <div>Only admins can access this page.</div>;
  }

  return (
    <div className="add-python-course">
      <h1>📚 Add New Course + Session</h1>
      <div className="course-preview">
        <h2>{newCourseData.title}</h2>
        <p className="course-description">{newCourseData.description}</p>
        <div className="topics-preview">
          <h3>Topics ({newCourseData.topics.length}):</h3>
          <ul>
            {newCourseData.topics.map((topic, idx) => (
              <li key={idx}>
                <strong>{topic.name}</strong>
                <span className="hours-badge">{topic.hours}h</span>
                <div className="topic-description">{topic.description}</div>
              </li>
            ))}
          </ul>
        </div>
        <p className="session-info">
          One session will be created and linked to this course (start: 7 days from today, duration: 12 weeks).
          Edit <code>src/utils/newCourseData.js</code> to change the course title, description, or topics before adding.
        </p>
      </div>
      <button
        onClick={handleAddCourseAndSession}
        disabled={loading}
        className="add-course-btn"
      >
        {loading ? '⏳ Creating course and session...' : '✨ Add course + session'}
      </button>
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AddNewCourse;
